import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
} from "@mui/material";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  LARGE_FORM_MODAL_SX,
  STANDARD_FORM_DIALOG_PROPS,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
} from "../../../utils/formModalStyles";
import { getAllDepartment } from "../../../services/departmentService";
import { format } from "date-fns";
import { getAllUserRoles } from "../../../services/UserRoleService";
import { createStaff, updateStaffByUserId } from "../../../services/StaffService";
import {
  validateOrgTeamUserFields,
  touchFormikErrorFields,
  buildStaffExcludeContext,
} from "../../../utils/orgUserUniqueness";
import subscriptionRoleLimitsService from "../../../services/subscriptionRoleLimitsService";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useAuth } from "../../../context/AuthContext.jsx";
import { formatUserRoleOptionLabel } from "../../../utils/pathRoleUtils";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import {
  filterDepartmentsForPackage,
  filterStaffRolesForPackage,
} from "../../../utils/subscriptionScopeUtils";

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Minimum 2 characters")
    .matches(/^[a-zA-Z0-9\s\-'.,&()]+$/, "Name must contain at least one letter and can only contain letters, numbers, spaces, and common punctuation")
    .test("has-letter", "Name must contain at least one letter (not just numbers)", (value) => {
      if (!value) return true; // Let required handle empty values
      return /[a-zA-Z]/.test(value); // Must contain at least one letter
    }),
  email: Yup.string().email("Invalid email"),
  education: Yup.string().required("Education is required"),
  aadhar: Yup.string()
    .required("Aadhar is required")
    .matches(/^\d{12}$/, "Must be 12-digit"),
  mobile: Yup.string()
    .required("Mobile is required")
    .matches(/^\d{10}$/, "Must be 10-digit"),
  joiningDate: Yup.date().required("Joining date required"),
  address: Yup.string().required("Address is required"),
  userRole: Yup.string().required("User Role is required"),
});

const AddNewDoctor = ({ open, onClose, doctor_info = {} }) => {
  const { user } = useAuth();
  const { isPremiumPackage, hasPathPackageAccess } = useSubscriptionCapabilities();
  const [department_list, setDepartmentList] = useState([]);
  const [role_list, setRoleList] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupData, setPopupData] = useState({ mobile: "", password: "" });
  const [roleLimitCheck, setRoleLimitCheck] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Stable default date for new staff (only created once)
  const defaultJoiningDate = useMemo(() => dayjs(), []);

  const visibleDepartments = useMemo(
    () =>
      filterDepartmentsForPackage(department_list, {
        isPremium: isPremiumPackage,
        keepIds: [doctor_info?.departmentId],
      }),
    [department_list, isPremiumPackage, doctor_info?.departmentId]
  );

  const visibleRoles = useMemo(
    () =>
      filterStaffRolesForPackage(role_list, {
        isPremium: isPremiumPackage,
        hasPathPackageAccess,
        keepRoleNames: [doctor_info?.userRole],
        excludeRoleNames: ["doctor", "surgeon"],
      }),
    [
      role_list,
      isPremiumPackage,
      hasPathPackageAccess,
      doctor_info?.userRole,
    ]
  );

  useEffect(() => {
    fetchDepartments();
    fetchUserRoles();
  }, []);

  const fetchDepartments = async () => {
    try {
      const data = await getAllDepartment();
      setDepartmentList(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUserRoles = async () => {
    try {
      const data = await getAllUserRoles();
      setRoleList(data);
    } catch (error) {
      console.log(error);
    }
  };

  // const isUnique = async (field, value, currentId = null) => {
  //   try {
  //     const allStaff = await getAllStaff();
  //     return !allStaff.some(
  //       (staff) => staff[field] === value && staff.id !== currentId
  //     );
  //   } catch (error) {
  //     console.error("Uniqueness check failed:", error);
  //     return true;
  //   }
  // };

  const formik = useFormik({
    initialValues: {
      name: doctor_info?.name || "",
      address: doctor_info?.address || "",
      aadhar: doctor_info?.aadhar || "",
      departmentId: doctor_info?.departmentId || "",
      departmentName: "",  
      education: doctor_info?.education || "",
      email: doctor_info?.email || "",
      joiningDate: doctor_info?.id && doctor_info?.joiningDate
        ? dayjs(doctor_info.joiningDate) 
        : (doctor_info?.id ? null : defaultJoiningDate), // Today's date for new staff, null for updates without date (user can set)
      mobile: doctor_info?.mobile || "",
      registrationNumber: doctor_info?.registrationNumber || "",
      specialty: doctor_info?.specialty || "",
      userRole: doctor_info?.userRole || "",
      password: doctor_info?.password || "", 
    },
    enableReinitialize: true,
    validationSchema,  
    validateOnBlur: true,
    validateOnChange: false,
    context: { id: doctor_info?.id }, 
    validate: async (values) => {
      const excludeContext = buildStaffExcludeContext(doctor_info);
      return validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
    },
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      const excludeContext = buildStaffExcludeContext(doctor_info);
      const duplicateErrors = await validateOrgTeamUserFields(values, excludeContext, [
        ["aadhar", "mobile"],
        ["email", "registrationNumber"],
      ]);
      if (Object.keys(duplicateErrors).length > 0) {
        Object.entries(duplicateErrors).forEach(([field, message]) => {
          setFieldError(field, message);
          setFieldTouched(field, true, false);
        });
        return;
      }

      // Check if updating staff - only Admin or Super Admin can change roles
      if (doctor_info && doctor_info.id) {
        // Get current user ID from user object or localStorage
        const storedUser = localStorage.getItem("user");
        let currentUserId = user?.id;
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser?.id) {
              currentUserId = parsedUser.id;
            }
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }
        
        // Check if user is editing themselves
        const isEditingSelf = doctor_info?.userId && currentUserId && 
          String(doctor_info.userId) === String(currentUserId);
        
        if (isEditingSelf) {
          const currentUserRole = (user?.roleName || "").toLowerCase();
          const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
          const isAdmin = currentUserRole === "admin";
          const isSuperAdmin = normalizedUserRole === "super_admin";
          
          // Prevent Admin and Super Admin from changing their own role
          if ((isAdmin || isSuperAdmin) && values.userRole !== doctor_info.userRole) {
            alert(isAdmin ? "Admin cannot change their own role" : "Super Admin cannot change their own role");
            return;
          }
        }
        
        const currentUserRole = (user?.roleName || "").toLowerCase();
        const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
        const isCurrentUserAdmin = 
          currentUserRole === "admin" || 
          normalizedUserRole === "super_admin";
        
        // Prevent role changes for non-admin users
        if (!isCurrentUserAdmin && values.userRole !== doctor_info.userRole) {
          alert("Only Admin or Super Admin can change user roles");
          return;
        }
      }

      // Clean up the data: convert empty strings to null for optional fields
      const req_Data = {
        ...values,
        joiningDate: format(new Date(values.joiningDate), "yyyy-MM-dd"),
        password: doctor_info && Object.keys(doctor_info).length > 0 ? values.password : "Staff@123",
        // Convert empty strings to null for optional fields
        departmentId: values.departmentId && values.departmentId !== "" ? values.departmentId : null,
        departmentName: values.departmentName && values.departmentName !== "" ? values.departmentName : null,
        specialty: values.specialty && values.specialty !== "" ? values.specialty : null,
        registrationNumber: values.registrationNumber && values.registrationNumber !== "" ? values.registrationNumber : null,
      };

      try {
        setIsSubmitting(true);
        if (doctor_info && doctor_info.id) {
          await updateStaffByUserId(doctor_info.userId, req_Data);
          alert("Staff updated successfully");
          handleFinalClose();
        } else {
          await createStaff(req_Data);
          alert("Staff created successfully");
          setPopupData({
            mobile: req_Data.mobile,
            password: req_Data.password,
          });
          setOpenPopup(true);
        }
      } catch (error) {
        console.error("Error saving staff:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to save staff";
        alert(`Error: ${errorMessage}`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Check subscription role limits when userRole changes (only for new staff, not updates)
  // This must be after formik initialization
  useEffect(() => {
    const checkRoleLimit = async () => {
      const userRole = formik.values.userRole;
      const organizationId = user?.organizationId;

      if (!userRole || !organizationId) {
        setRoleLimitCheck(null);
        return;
      }

      // Check if this is an update and if the role is the same
      const isUpdate = doctor_info && doctor_info.id;
      const originalRole = doctor_info?.userRole;
      const roleChanged = isUpdate && originalRole && originalRole !== userRole;

      setCheckingLimit(true);
      try {
        const limitCheck = await subscriptionRoleLimitsService.checkRoleLimit(
          organizationId,
          userRole
        );
        
        // If updating and role is the same, allow it (user is already in this role)
        if (isUpdate && !roleChanged && limitCheck.limit !== -1) {
          // User is already in this role, so allow the update
          limitCheck.allowed = true;
          limitCheck.message = `Updating existing ${userRole} user. ${limitCheck.message}`;
        }
        
        console.log("[EditRole] Role limit check result:", limitCheck);
        setRoleLimitCheck(limitCheck);
      } catch (error) {
        console.error("Error checking role limit:", error);
        setRoleLimitCheck(null);
      } finally {
        setCheckingLimit(false);
      }
    };

    // Debounce the check to avoid too many API calls
    const timeoutId = setTimeout(() => {
      checkRoleLimit();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formik.values.userRole, user?.organizationId, doctor_info?.id, doctor_info?.userRole]);
  
  const handleFinalClose = () => {
    setOpenPopup(false);
    onClose();
    formik.resetForm();
  };

  return (
    <BasicModal
      open={open}
      onClose={handleFinalClose}
      sx={LARGE_FORM_MODAL_SX}
      title={doctor_info && doctor_info.id ? "Update Staff" : "Create Staff"}
    >
      <Box display="flex" flexDirection="column">
        <Box>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Name"
                  value={formik.values.name}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    
                    // Allow alphanumeric, spaces, and common punctuation (hyphens, apostrophes, periods, commas, ampersands, parentheses)
                    if (inputValue === "" || /^[a-zA-Z0-9\s\-'.,&()]*$/.test(inputValue)) {
                      // Capitalize first letter
                      if (inputValue.length > 0) {
                        inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                      }
                      
                      formik.setFieldValue("name", inputValue);
                    }
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow email characters including @ symbol (letters, numbers, dots, hyphens, underscores, @, and +)
                    if (value === "" || /^[a-zA-Z0-9._+-@]*$/.test(value)) {
                      formik.setFieldValue("email", value);
                    }
                  }}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="education"
                  name="education"
                  label="Education"
                  value={formik.values.education}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.education && Boolean(formik.errors.education)
                  }
                  helperText={
                    formik.touched.education && formik.errors.education
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="aadhar"
                  name="aadhar"
                  label="Aadhar"
                  value={formik.values.aadhar}
                  onChange={(e) => {
                    // Only allow digits and limit to 12 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                    formik.setFieldValue("aadhar", value);
                  }}
                  onBlur={formik.handleBlur}
                  inputProps={{ maxLength: 12 }}
                  error={formik.touched.aadhar && Boolean(formik.errors.aadhar)}
                  helperText={formik.touched.aadhar && formik.errors.aadhar}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="mobile"
                  name="mobile"
                  label="Mobile"
                  value={formik.values.mobile}
                  onChange={(e) => {
                    // Only allow digits and limit to 10 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    formik.setFieldValue("mobile", value);
                  }}
                  inputProps={{ maxLength: 10 }}
                  error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                  helperText={formik.touched.mobile && formik.errors.mobile}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="registrationNumber"
                  name="registrationNumber"
                  label="Registration Number"
                  value={formik.values.registrationNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.registrationNumber &&
                    Boolean(formik.errors.registrationNumber)
                  }
                  helperText={
                    formik.touched.registrationNumber &&
                    formik.errors.registrationNumber
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Joining Date"
                    format="YYYY-MM-DD"
                    value={formik.values.joiningDate}
                    onChange={(value) =>
                      formik.setFieldValue("joiningDate", value)
                    }
                    minDate={doctor_info?.id ? undefined : dayjs()} // For create: only today allowed
                    maxDate={dayjs()} // Disable future dates for both create and update
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          formik.touched.joiningDate &&
                          Boolean(formik.errors.joiningDate),
                        helperText:
                          formik.touched.joiningDate &&
                          formik.errors.joiningDate,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  id="address"
                  name="address"
                  label="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="userRole"
                  name="userRole"
                  fullWidth
                  select
                  label="User Role"
                  value={formik.values.userRole}
                  onChange={(e) =>
                    formik.setFieldValue("userRole", e.target.value)
                  }
                  error={
                    formik.touched.userRole && Boolean(formik.errors.userRole)
                  }
                  helperText={
                    (() => {
                      // Check if updating staff - only Admin or Super Admin can change roles
                      if (doctor_info && doctor_info.id) {
                        // Get current user ID from user object or localStorage
                        const storedUser = localStorage.getItem("user");
                        let currentUserId = user?.id;
                        if (storedUser) {
                          try {
                            const parsedUser = JSON.parse(storedUser);
                            if (parsedUser?.id) {
                              currentUserId = parsedUser.id;
                            }
                          } catch (e) {
                            console.error("Error parsing stored user:", e);
                          }
                        }
                        
                        // Check if user is editing themselves
                        const isEditingSelf = doctor_info?.userId && currentUserId && 
                          String(doctor_info.userId) === String(currentUserId);
                        
                        if (isEditingSelf) {
                          const currentUserRole = (user?.roleName || "").toLowerCase();
                          const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
                          const isAdmin = currentUserRole === "admin";
                          const isSuperAdmin = normalizedUserRole === "super_admin";
                          
                          if (isAdmin) {
                            return "Admin cannot change their own role";
                          } else if (isSuperAdmin) {
                            return "Super Admin cannot change their own role";
                          }
                        }
                        
                        const currentUserRole = (user?.roleName || "").toLowerCase();
                        const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
                        const isCurrentUserAdmin = 
                          currentUserRole === "admin" || 
                          normalizedUserRole === "super_admin";
                        
                        // Show message if current user is NOT admin or super_admin
                        if (!isCurrentUserAdmin) {
                          return "Only Admin or Super Admin can change user roles";
                        }
                      }
                      return formik.touched.userRole && formik.errors.userRole;
                    })()
                  }
                  disabled={
                    (() => {
                      // Check if updating staff - only Admin or Super Admin can change roles
                      if (doctor_info && doctor_info.id) {
                        // Get current user ID from user object or localStorage
                        const storedUser = localStorage.getItem("user");
                        let currentUserId = user?.id;
                        if (storedUser) {
                          try {
                            const parsedUser = JSON.parse(storedUser);
                            if (parsedUser?.id) {
                              currentUserId = parsedUser.id;
                            }
                          } catch (e) {
                            console.error("Error parsing stored user:", e);
                          }
                        }
                        
                        // Check if user is editing themselves
                        const isEditingSelf = doctor_info?.userId && currentUserId && 
                          String(doctor_info.userId) === String(currentUserId);
                        
                        if (isEditingSelf) {
                          // Admin and Super Admin cannot change their own role
                          return true;
                        }
                        
                        const currentUserRole = (user?.roleName || "").toLowerCase();
                        const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
                        const isCurrentUserAdmin = 
                          currentUserRole === "admin" || 
                          normalizedUserRole === "super_admin";
                        
                        // Disable if current user is NOT admin or super_admin
                        return !isCurrentUserAdmin;
                      }
                      return false;
                    })()
                  }
                >
                  {visibleRoles.map((role) => {
                      const roleNameLower = role.roleName.toLowerCase();
                      // Normalize role name by replacing spaces/underscores for comparison
                      const normalizedRoleName = roleNameLower.replace(/[\s_]+/g, "_");
                      const isDisabled = 
                        roleNameLower === "admin" || 
                        normalizedRoleName === "super_admin";
                      return (
                        <MenuItem 
                          key={role.roleName} 
                          value={role.roleName}
                          disabled={isDisabled}
                        >
                          {formatUserRoleOptionLabel(role.roleName, role.username)}
                        </MenuItem>
                      );
                    })}
                </TextField>
              </Grid>

              {/* Subscription Role Limit Warning */}
              {roleLimitCheck && roleLimitCheck.limit !== undefined && roleLimitCheck.limit !== -1 && (
                <Grid item xs={12}>
                  <Alert 
                    severity={!roleLimitCheck.allowed || roleLimitCheck.limit === 0 ? "error" : "info"}
                    sx={{ mt: 1 }}
                  >
                    {roleLimitCheck.limit === 0 ? (
                      <>
                        <strong>No Users Allowed:</strong> This subscription does not allow {doctor_info?.id ? "assigning" : "creating"} {formik.values.userRole} users.
                      </>
                    ) : !roleLimitCheck.allowed ? (
                      <>
                        <strong>Limit Reached:</strong> Cannot {doctor_info?.id ? "change role to" : "create more"} {formik.values.userRole} users. 
                        Current: {roleLimitCheck.currentCount}/{roleLimitCheck.limit}
                      </>
                    ) : (
                      <>
                        <strong>Subscription Limit:</strong> {formik.values.userRole} users: {roleLimitCheck.currentCount}/{roleLimitCheck.limit} used
                      </>
                    )}
                  </Alert>
                </Grid>
              )}

              {formik.values.userRole === "staff" && (
                <Grid item xs={12}>
                  <TextField
                    id="departmentId"
                    name="departmentId"
                    fullWidth
                    select
                    label="Department"
                    value={formik.values.departmentId}
                    // onChange={(e) =>
                    //   formik.setFieldValue("departmentId", e.target.value)
                    // }
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      formik.setFieldValue("departmentId", selectedId);

                      // Set the departmentName hidden field
                      const selectedDept = department_list.find(d => d.id === selectedId);
                      if (selectedDept) {
                        formik.setFieldValue("departmentName", selectedDept.name);
                      } else {
                        formik.setFieldValue("departmentName", "");
                      }
                    }}
                  >
                    {visibleDepartments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* <TextField
                    type="hidden"
                    id="departmentName"
                    name="departmentName"
                    value={formik.values.departmentName}
                  /> */}
                </Grid>
              )}
              {(() => {
                // Check if updating a staff member
                if (!doctor_info || !doctor_info.id) {
                  return null; // Don't show password field when creating new staff
                }

                // Check if the staff member being updated is admin or super admin
                const staffRole = (doctor_info.userRole || "").toLowerCase();
                const normalizedStaffRole = staffRole.replace(/[\s_]+/g, "_");
                const isStaffAdminOrSuperAdmin = 
                  staffRole === "admin" || 
                  normalizedStaffRole === "super_admin";

                // Check if current user is admin or super admin
                const currentUserRole = (user?.roleName || "").toLowerCase();
                const normalizedUserRole = currentUserRole.replace(/[\s_]+/g, "_");
                const isCurrentUserAdmin = 
                  currentUserRole === "admin" || 
                  normalizedUserRole === "super_admin";

                // Hide password field if staff is admin/super admin AND current user is NOT admin/super admin
                if (isStaffAdminOrSuperAdmin && !isCurrentUserAdmin) {
                  return null;
                }

                // Show password field for all other cases
                return (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="Password"
                      type="text"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                );
              })()}

              <Grid item xs={12}>
                <FormModalActions
                  onCancel={handleFinalClose}
                  loading={isSubmitting}
                  submitDisabled={
                    isSubmitting ||
                    checkingLimit ||
                    (roleLimitCheck &&
                      roleLimitCheck.limit !== -1 &&
                      (roleLimitCheck.limit === 0 || !roleLimitCheck.allowed))
                  }
                  onSubmit={async () => {
                    const errors = await formik.validateForm();
                    if (Object.keys(errors).length > 0) {
                      touchFormikErrorFields(formik, errors);
                      return;
                    }
                    formik.submitForm();
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>

      {/* ✅ Success Popup */}
      <Dialog
        open={openPopup}
        onClose={handleFinalClose}
        {...STANDARD_FORM_DIALOG_PROPS}
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Staff Created</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>Mobile:</strong> {popupData.mobile}
          </Typography>
          <Typography>
            <strong>Password:</strong> {popupData.password}
          </Typography>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={handleFinalClose} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </BasicModal>
  );
};
export default AddNewDoctor;
